import { useHitMap } from '@/hooks/useHitMap'
import React, { useState } from 'react'
import { BackButton } from '../../components/layout/NavBar'
import { Services } from '@/services/services'
import { useLocation } from 'react-router-dom'

import { CurrentSession } from '@/services/repository/session'
import { MainPageLayout } from '@/components/layout/MainLayout'
import { HitMapComponent } from '@/components/Map/HitMap'
import { StandaloneDropdown } from '@/components/forms/common/StandaloneDropdown'
import { DateRange, DateTimeRangeBody } from '@/popups/dateTimeRange'
import { formatDate, getTodayRange } from '@/common/time'
import { useModal } from '@/components/modal/Modal'
import { CommonUsernameInput } from '@/components/forms/common/user/input'
import { CommonMachinenameInput } from '@/components/forms/common/machine/input'

import './HitMap.css'
import { HIG_HIT, MID_HIT } from '@/services/repository/hits'
import { useTranslations } from '@/hooks/useTranslations'
import { Spinner } from '@/components/spinner'

interface HitRange {
  min: number | undefined
  max: number | undefined
}

export const hitsMap: { [key: string]: HitRange } = {
  'hi': {
    min: HIG_HIT,
    max: 9999
  },
  'mid': {
    min: MID_HIT,
    max: HIG_HIT
  },
  'low': {
    min: 0,
    max: MID_HIT
  },
  'all': {
    min: undefined,
    max: undefined
  }
}

export interface HitMapNavData {
  range: DateRange
  hitLvl: string
  machinePoint: string
  username: string
  hitRange: HitRange
}

export interface HitMapNavFieldMatrix {
  range: boolean
  username: boolean
  machineName: boolean
}

export const HitMapNavURLParams = (data: HitMapNavData) => {
  return new URLSearchParams({
    username: data.username || '', machine: data.machinePoint || '', hitLvl: data.hitLvl,
    from: data.range.from?.toISOString() || '', to: data.range.to?.toISOString() || '',
    minHit: String(data.hitRange.min ?? ''), maxHit: String(data.hitRange.max ?? '')
  }).toString()
}

export const HitMapNav = ({ services, session, data, setter, fieldMatrix }: {
  services: Services,
  session: CurrentSession,
  data: HitMapNavData,
  setter: (data: HitMapNavData) => void,
  fieldMatrix: HitMapNavFieldMatrix
}) => {
  // const [range, setRange] = useState<DateRange>(getTodayRange())
  // const [hitLvl, setHitLvl] = useState<string>('all')
  // const [machinePoint, setMachinePoint] = useState<string>('')
  // const [username, setUsername] = useState<string>('')
  const { openModal } = useModal()

  function update(n: any) {
    const hitLvl = n.hitLvl || data.hitLvl
    setter({
      ...data,
      ...n,
      hitRange: hitsMap[hitLvl]
    })
  }

  return <div className='hitmap-navigation'>
    {fieldMatrix.range ? <div className='top-icon-button' onClick={() => openModal((props) => DateTimeRangeBody({
      ...props,
      services,
      session,
      translates: session.translates,
      range: data.range,
      setter: (range) => {
        update({
          range
        })
      }
    }))}>
      <i className='svgfont-calendar' />
      <span>
        {session.translates.for('map.from_to_datetime').replace('{{from_date}}', formatDate(data.range.from)).replace('{{to_date}}', formatDate(data.range.to))}
      </span>
    </div> : null }

    <form className='main-form-slim main-form-two-col'>
      <div className='form-field'>
        <div>
          <StandaloneDropdown
            value={data.hitLvl}
            onChange={(hitLvl) => {
              update({
                hitLvl
              })
            }}
            options={Object.keys(hitsMap).map(row => ({
              value: row,
              label: session.translates.for(`dicts.hitscale.${row}`),
            }))}
            readOnly={false}
          />
        </div>
      </div>
      {fieldMatrix.username ? <div className='form-field'>
        <CommonUsernameInput fieldName='username' formName='hitmap' setValue={(username) => {
          update({
            username
          })
        }} value={data.username || ''} services={services} session={session} translates={session.translates} />
      </div> : null}
      {fieldMatrix.machineName ? <div className='form-field'>
        <CommonMachinenameInput fieldName='machine' formName='hitmap' setValue={(machinePoint) => {
          update({
            machinePoint
          })
        }} value={data.machinePoint || ''} services={services} session={session} translates={session.translates} />
      </div> : null}
    </form>
  </div>
}

export const CommunalHitsMap = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const location = useLocation();
  const defaultRange = getTodayRange();
  const params = new URLSearchParams(location.search);

  const getDecodedDate = (param: string | null, fallback: Date) =>
    param ? new Date(decodeURIComponent(param)) : fallback;

  const [navData, setNavData] = useState<HitMapNavData>({
    range: {
      from: getDecodedDate(params.get("from"), defaultRange.from),
      to: getDecodedDate(params.get("to"), defaultRange.to),
    },
    hitLvl: 'all',
    machinePoint: params.get("machine") || '',
    username: params.get("username") || '',
    hitRange: hitsMap.all,
  });

  const translates = useTranslations(services, session)

  const { rows, error } = useHitMap(services, `/api/communal/hits/all?${HitMapNavURLParams(navData)}`)

  if (!translates) {
    return <Spinner />
  }


  return <MainPageLayout pageName='communal-hitmap' services={services} translates={translates} session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}>

    <HitMapNav session={session} services={services} data={navData} setter={setNavData} fieldMatrix={{
      range: true,
      username: true,
      machineName: true
    }} />

    <div className='default-map-container'>
      <HitMapComponent error={error} rows={rows} services={services} session={session} />
    </div>
  </MainPageLayout>
}
